import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Copy, Edit3, Eye } from "lucide-react";
import React, { useCallback, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  className?: string;
  isFullScreen?: boolean;
}

export function MarkdownEditor({
  value,
  onChange,
  placeholder = "Enter markdown text...",
  label = "Markdown Editor",
  className = "",
  isFullScreen = false,
}: MarkdownEditorProps) {
  const [activeTab, setActiveTab] = useState<"edit" | "preview">("preview");
  const [previewHeight, setPreviewHeight] = useState<number>(420);
  const [isResizing, setIsResizing] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef<number>(0);
  const startHeightRef = useRef<number>(0);

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      startYRef.current = e.clientY;
      startHeightRef.current = previewHeight;
    },
    [previewHeight],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;

      const deltaY = e.clientY - startYRef.current;
      const newHeight = Math.max(120, Math.min(600, startHeightRef.current + deltaY));
      setPreviewHeight(newHeight);
    },
    [isResizing],
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Add event listeners for mouse move and up
  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "ns-resize";
      document.body.style.userSelect = "none";
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  return (
    <div className={`space-y-2 w-full ${className}`}>
      <div className="flex items-center justify-between">
        <Label htmlFor="markdown-editor">{label}</Label>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleCopyToClipboard} className="h-8">
            <Copy className="h-4 w-4 mr-1" />
            Copy
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "edit" | "preview")}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="edit" className="flex items-center gap-2">
            <Edit3 className="h-4 w-4" />
            Edit
          </TabsTrigger>
          <TabsTrigger value="preview" className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Preview
          </TabsTrigger>
        </TabsList>

        <TabsContent value="edit" className="mt-2">
          <Textarea
            id="markdown-editor"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={`w-full resize-y transition-all duration-300 ${isFullScreen ? "h-96" : "h-40"}`}
            style={{ resize: "vertical" }}
          />
        </TabsContent>

        <TabsContent value="preview" className="mt-2">
          <div className="relative">
            <div
              ref={previewRef}
              className="w-full border rounded-md p-4 bg-background overflow-auto"
              style={{ height: `${previewHeight}px` }}
            >
              {value.trim() ? (
                <div className="prose prose-sm max-w-none dark:prose-invert prose-headings:font-semibold prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-p:text-sm prose-li:text-sm prose-strong:font-semibold prose-em:italic">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h1: ({ children }) => <h1 className="text-2xl font-bold mb-4 text-foreground">{children}</h1>,
                      h2: ({ children }) => <h2 className="text-xl font-semibold mb-3 text-foreground">{children}</h2>,
                      h3: ({ children }) => <h3 className="text-lg font-semibold mb-2 text-foreground">{children}</h3>,
                      p: ({ children }) => <p className="mb-3 text-sm leading-relaxed text-foreground">{children}</p>,
                      ul: ({ children }) => <ul className="mb-3 ml-4 list-disc text-sm text-foreground">{children}</ul>,
                      ol: ({ children }) => (
                        <ol className="mb-3 ml-4 list-decimal text-sm text-foreground">{children}</ol>
                      ),
                      li: ({ children }) => <li className="mb-1 text-sm text-foreground">{children}</li>,
                      strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
                      em: ({ children }) => <em className="italic text-foreground">{children}</em>,
                      blockquote: ({ children }) => (
                        <blockquote className="border-l-4 border-muted-foreground pl-4 my-3 italic text-muted-foreground">
                          {children}
                        </blockquote>
                      ),
                      code: ({ children }) => (
                        <code className="bg-muted px-1 py-0.5 rounded text-xs font-mono text-foreground">
                          {children}
                        </code>
                      ),
                      pre: ({ children }) => (
                        <pre className="bg-muted p-3 rounded-md overflow-x-auto text-xs font-mono text-foreground">
                          {children}
                        </pre>
                      ),
                      hr: () => <hr className="my-4 border-t border-border" />,
                      // Custom table styling
                      table: ({ children }) => (
                        <div className="overflow-x-auto my-4">
                          <table className="min-w-full border border-border rounded-md">{children}</table>
                        </div>
                      ),
                      thead: ({ children }) => <thead className="bg-muted">{children}</thead>,
                      tbody: ({ children }) => <tbody className="divide-y divide-border">{children}</tbody>,
                      tr: ({ children }) => <tr className="hover:bg-muted/50">{children}</tr>,
                      th: ({ children }) => (
                        <th className="px-3 py-2 text-left text-xs font-medium text-foreground border-r border-border last:border-r-0">
                          {children}
                        </th>
                      ),
                      td: ({ children }) => (
                        <td className="px-3 py-2 text-xs text-foreground border-r border-border last:border-r-0">
                          {children}
                        </td>
                      ),
                    }}
                  >
                    {value}
                  </ReactMarkdown>
                </div>
              ) : (
                <p className="text-muted-foreground italic">No content to preview</p>
              )}
            </div>
            {/* Drag handle */}
            <div
              className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize bg-transparent hover:bg-muted/20 transition-colors flex items-center justify-center group"
              onMouseDown={handleMouseDown}
            >
              <div className="w-8 h-1 bg-muted-foreground/30 rounded-full group-hover:bg-muted-foreground/60 transition-colors" />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
